/**
 * LanguageSwitcher — compact pill for switching wallet language from
 * anywhere. Shows the current language's ISO code plus a chevron; opens
 * a small popover with every supported language. Tapping "English"
 * resets to the source language and clears no state (translations for
 * other languages stay cached).
 *
 * Rendered in the wallet sidebar footer + mobile top header so a
 * consumer never has to hunt through Settings to change language.
 */
import { useEffect, useRef, useState } from "react";
import { Languages, Check, ChevronDown } from "lucide-react";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import { emitSignal } from "@/lib/wallet-signal";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

// All wallet-supported languages. First the 11 South African official
// languages, then major migrant-community languages available via the
// LLM translator.
export const WALLET_LANGUAGES = [
  { code: "en",  label: "English",     native: "English" },
  { code: "af",  label: "Afrikaans",   native: "Afrikaans" },
  { code: "zu",  label: "isiZulu",     native: "isiZulu" },
  { code: "xh",  label: "isiXhosa",    native: "isiXhosa" },
  { code: "st",  label: "Sesotho",     native: "Sesotho" },
  { code: "tn",  label: "Setswana",    native: "Setswana" },
  { code: "nso", label: "Sepedi",      native: "Sepedi" },
  { code: "ts",  label: "Xitsonga",    native: "Xitsonga" },
  { code: "ss",  label: "siSwati",     native: "siSwati" },
  { code: "ve",  label: "Tshivenda",   native: "Tshivenda" },
  { code: "nr",  label: "isiNdebele",  native: "isiNdebele" },
  { code: "es",  label: "Spanish",     native: "Español" },
  { code: "pt",  label: "Portuguese",  native: "Português" },
  { code: "fr",  label: "French",      native: "Français" },
  { code: "sw",  label: "Swahili",     native: "Kiswahili" },
  { code: "sn",  label: "Shona",       native: "chiShona" },
];

export default function LanguageSwitcher({ variant = "sidebar" }) {
  const { prefs, update } = useAccessibility();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current = WALLET_LANGUAGES.find(l => l.code === prefs.language) || WALLET_LANGUAGES[0];

  const pick = (code) => {
    if (code !== prefs.language) {
      update({ language: code });
      emitSignal("wallet.language.changed.v1", { from: prefs.language, to: code });
    }
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative" data-testid={`language-switcher-${variant}`} data-no-translate="true">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="language-switcher-toggle"
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Languages className="w-3.5 h-3.5" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[11px] uppercase tracking-widest" style={{ color: GOLD }}>Language</div>
          <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }} data-no-translate="true">{current.native}</div>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>

      {open && (
        <div
          data-testid="language-switcher-menu"
          data-no-translate="true"
          className="absolute z-40 rounded-2xl bg-white border border-slate-100 shadow-2xl p-1 max-h-[60vh] overflow-y-auto"
          style={{ bottom: "calc(100% + 6px)", left: 0, right: 0, minWidth: 240 }}
          role="listbox"
        >
          {WALLET_LANGUAGES.map((l) => {
            const selected = l.code === prefs.language;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={selected}
                data-testid={`language-option-${l.code}`}
                onClick={() => pick(l.code)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left hover:bg-slate-50 transition"
                style={{ background: selected ? `${GOLD}18` : "transparent" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{l.native}</div>
                  {l.native !== l.label && (
                    <div className="text-[11px] text-slate-500">{l.label}</div>
                  )}
                </div>
                {selected && <Check className="w-3.5 h-3.5" style={{ color: NAVY }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
